import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AddressAutocomplete.css";

const API = "https://nominatim.openstreetmap.org/search";

function debounce(fn, ms = 350) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export default function AddressAutocomplete({ value, onSelect, placeholder = "Адрес" }) {
  const [input, setInput] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Закрытие выпадающего списка при клике вне
  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current || boxRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const doSearch = useMemo(
    () =>
      debounce(async (q) => {
        if (!q || q.trim().length < 3) {
          setItems([]);
          return;
        }
        try {
          const params = new URLSearchParams({
            q,
            format: "json",
            addressdetails: "1",
            limit: "8",
            countrycodes: "gr", // 👉 ограничим Грецией; убери если нужно шире
          });
          const res = await fetch(`${API}?${params.toString()}`, {
            headers: {
              Accept: "application/json",
              "Accept-Language": "el", // 👉 просим греческие подписи
            },
          });
          const data = await res.json();

          setItems(
            data.map((it) => {
              const a = it.address || {};
              const city =
                a.city ||
                a.town ||
                a.village ||
                a.municipality ||
                a.state_district ||
                "";

              const street = a.road || a.pedestrian || a.footway || a.path || "";
              const house = a.house_number || "";

              // Красивый лейбл: "Оδός … 12, Αθήνα" (если есть номер)
              const niceLabel =
                (street || house)
                  ? `${street}${house ? " " + house : ""}${city ? ", " + city : ""}`
                  : it.display_name;

              return {
                id: it.place_id,
                label: niceLabel || it.display_name,
                lat: parseFloat(it.lat),
                lon: parseFloat(it.lon),
                city,
                address_line: it.display_name, // полный адрес (локализованный)
                street,
                house_number: house,
              };
            })
          );
          setOpen(true);
        } catch (e) {
          console.error("Nominatim error:", e);
          setItems([]);
          setOpen(false);
        }
      }, 350),
    []
  );

  const onChange = (e) => {
    const v = e.target.value;
    setInput(v);
    doSearch(v);
  };

  const selectItem = (item) => {
    setInput(item.label);
    setOpen(false);
    onSelect?.({
      address_line: item.address_line, // полный адрес (греческий)
      city: item.city,
      atitude: item.lat,
      longitude: item.lon,
      street: item.street || "",
      house_number: item.house_number || "",
    });
  };

  return (
    <div className="address-autocomplete" ref={boxRef}>
      <input
        type="text"
        value={input}
        onChange={onChange}
        placeholder={placeholder}
        className="address-input"
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div className="address-dropdown">
          {items.map((it) => (
            <div
              key={it.id}
              className="address-item"
              onClick={() => selectItem(it)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {it.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
